import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import * as Icons from "lucide-react";
import { Pause, Play } from "lucide-react";

const STAGE_MS = 3200;
const kebabToPascal = (s) => s.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join("");

export function WorkflowReel({ reel, trackName, promptCount, slug }) {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(!reduced);
  const [visible, setVisible] = useState(true);
  const ref = useRef(null);
  const stages = reel?.stages || [];

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!playing || !visible || stages.length < 2) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % stages.length), STAGE_MS);
    return () => clearInterval(t);
  }, [playing, visible, stages.length]);

  if (stages.length === 0) return null;
  const s = stages[i];
  const Icon = Icons[kebabToPascal(s.icon || "sparkles")] || Icons.Sparkles;

  return (
    <section ref={ref} className="relative mt-10 overflow-hidden border border-purple-500/30 bg-[#06040b]" aria-label={`${trackName} workflow preview`} data-testid={`workflow-reel-${slug}`}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(60% 80% at 20% 20%, rgba(168,85,247,.22), transparent 60%), radial-gradient(50% 60% at 85% 80%, rgba(192,132,252,.14), transparent 60%)" }} />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.035)_1px,transparent_1px)] bg-[size:36px_36px]" />

      <div className="relative flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-3 sm:px-7">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-purple-300" data-testid="workflow-reel-kicker">{reel.kicker || "The workflow · in 30 seconds"}</div>
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-zinc-400">
          <span data-testid="workflow-reel-counter">Stage {i + 1} / {stages.length}</span>
          <button type="button" onClick={() => setPlaying((p) => !p)} className="inline-flex min-h-9 items-center gap-1.5 border border-zinc-700 px-3 text-zinc-200 transition-colors hover:border-purple-400" aria-pressed={playing} data-testid="workflow-reel-toggle">
            {playing ? <Pause size={12} aria-hidden="true" /> : <Play size={12} aria-hidden="true" />}{playing ? "Pause" : "Play"}
          </button>
        </div>
      </div>

      <div className="relative grid gap-6 px-5 py-8 sm:px-7 md:grid-cols-[1fr_auto] md:items-center md:py-10">
        <div className="min-h-[190px] sm:min-h-[170px]">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={reduced ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={reduced ? undefined : { opacity: 0, y: -10 }} transition={{ duration: 0.45, ease: "easeOut" }}>
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center border border-purple-400/50 bg-purple-500/10 text-purple-200 shadow-[0_0_28px_-8px_rgba(168,85,247,.9)]"><Icon size={20} aria-hidden="true" /></span>
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">{s.label}</span>
              </div>
              <h3 className="mt-4 font-display text-2xl font-black leading-tight text-white sm:text-3xl" data-testid="workflow-reel-headline">{s.headline}</h3>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-300 sm:text-base">{s.line}</p>
              {s.guard && <p className="mt-3 inline-flex items-start gap-2 border-l-2 border-purple-400 pl-3 text-xs text-purple-100/90" data-testid="workflow-reel-guard"><Icons.ShieldCheck size={14} className="mt-0.5 shrink-0" aria-hidden="true" />{s.guard}</p>}
            </motion.div>
          </AnimatePresence>
        </div>

        <ol className="flex gap-2 md:flex-col md:gap-1.5" aria-label="Workflow stages">
          {stages.map((st, n) => (
            <li key={st.label}>
              <button type="button" onClick={() => { setI(n); setPlaying(false); }} className={`group flex items-center gap-2 text-left ${n === i ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`} aria-current={n === i ? "step" : undefined} data-testid={`workflow-reel-stage-${n}`}>
                <span className={`block h-1.5 w-6 transition-[background-color,width] duration-300 md:h-6 md:w-1.5 ${n === i ? "bg-purple-400" : n < i ? "bg-purple-500/40" : "bg-zinc-800"}`} />
                <span className="hidden text-[11px] uppercase tracking-widest md:inline">{st.label}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      <div className="relative h-1 w-full bg-zinc-900" aria-hidden="true">
        {playing && !reduced && <motion.div key={`bar-${i}`} className="h-full bg-purple-400" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: STAGE_MS / 1000, ease: "linear" }} />}
      </div>
      <div className="relative flex flex-wrap items-center justify-between gap-2 border-t border-white/10 px-5 py-3 text-xs text-zinc-400 sm:px-7">
        <span>{promptCount} copy-ready prompts · rights and policy check at every stage</span>
        <span className="text-zinc-600">{reel.footer || "No views, subscribers or income promised."}</span>
      </div>
    </section>
  );
}
